import { useCallback, useEffect, useState } from "react";
import { SYSTEMS, SYSTEM_ID } from "../integration";
import { IconClose } from "../icons";

/**
 * Faixa de avisos do Portal (Admin Cybersecurity) — falhas, manutenções e
 * comunicados enviados a este sistema.
 *
 * Busca em GET {portal}/api/security/notificacoes/ativas?sistema=rh
 * (endpoint público, sem autenticação, somente leitura) a cada 60s e
 * permite dispensar cada aviso individualmente (guardado no localStorage,
 * para não repetir o mesmo aviso já visto).
 */

type TipoAviso = "falha" | "manutencao" | "comunicado";

interface Aviso {
  id: number;
  tipo: TipoAviso;
  titulo: string;
  mensagem: string;
  sistema_alvo: string | null;
  criado_em: string;
  expira_em: string | null;
}

const DISMISSED_KEY = "sercamp-avisos-dispensados";
const POLL_MS = 60_000;

function readDismissed(): number[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: number[]) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage indisponível (modo privado etc.) — ignora */
  }
}

const TIPO_INFO: Record<TipoAviso, { label: string; className: string }> = {
  falha: { label: "Falha", className: "aviso-falha" },
  manutencao: { label: "Manutenção", className: "aviso-manutencao" },
  comunicado: { label: "Aviso", className: "aviso-comunicado" },
};

export default function AvisoSistema() {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [dismissed, setDismissed] = useState<number[]>(readDismissed);

  const fetchAvisos = useCallback(async () => {
    try {
      const url =
        `${SYSTEMS.portal}/api/security/notificacoes/ativas` +
        `?sistema=${encodeURIComponent(SYSTEM_ID)}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const body = (await res.json()) as Aviso[];
      if (Array.isArray(body)) setAvisos(body);
    } catch {
      /* Portal fora do ar — não quebra o app, só não exibe avisos agora */
    }
  }, []);

  useEffect(() => {
    fetchAvisos();
    const id = setInterval(fetchAvisos, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAvisos]);

  function dispensar(id: number) {
    setDismissed((prev) => {
      const next = [...prev, id];
      writeDismissed(next);
      return next;
    });
  }

  const visiveis = avisos.filter((a) => !dismissed.includes(a.id));
  if (visiveis.length === 0) return null;

  return (
    <div className="aviso-stack">
      {visiveis.map((a) => {
        const info = TIPO_INFO[a.tipo] ?? TIPO_INFO.comunicado;
        return (
          <div key={a.id} className={`aviso-banner ${info.className}`}>
            <span className="aviso-banner-tag">{info.label}</span>
            <div className="aviso-banner-text">
              <strong>{a.titulo}</strong>
              <span>{a.mensagem}</span>
            </div>
            <button
              className="icon-btn aviso-banner-close"
              onClick={() => dispensar(a.id)}
              title="Dispensar aviso"
              aria-label="Dispensar aviso"
            >
              <IconClose size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
